import { applySnapshot, flow, IModelType, types } from 'mobx-state-tree';
import { createArrayFromMap, getEnvironment } from './utils';
import { chunk, flatten, uniq } from 'lodash';
import NotificationToast from '../components/common/NotificationToast';
import { EventEmitter } from 'events';

const MAX_GET_PARAMS_LENGTH = 1500;

const STORE_STATE = { INITIAL: 0, LOADING: 1, ERROR: 2, LOADED: 3, DIRTY: 4 };

export function getBaseStore<T, S>(model: IModelType<any, any>, identifier: string, baseUrl: string, loadParams?: any) {
  return types
    .model('BaseStore', {
      datas: types.optional(types.map(model), {}),
      state: types.optional(types.number, STORE_STATE.INITIAL),
      miscDatas: types.optional(types.model({}), {}),
    })
    .views((self) => {
      const mapDataList = createArrayFromMap(self.datas);
      return {
        mapDataList(func: (t: T) => any): any[] {
          return mapDataList(func);
        },
        get dataList(): T[] {
          return Array.from(self.datas.values()) as T[];
        },
        getById(id: number): T | undefined {
          return self.datas.get(id.toString());
        },
        getFirst(): T | undefined {
          return self.datas.size == 0 ? undefined : self.datas.values().next().value;
        },
        isListLoaded(): boolean {
          if (self.state === STORE_STATE.LOADED) return true;
          return false;
        },
        isListLoading(): boolean {
          if (self.state === STORE_STATE.LOADING) return true;
          return false;
        },
        getByIds(ids: number[]): T[] | undefined {
          const datas: T[] = [];
          if (ids != null) {
            ids.map((id) => {
              const elem: T = self.datas.get(id.toString());
              if (elem !== null) {
                datas.push(elem);
              }
            });
          }
          return datas;
        },
      };
    })
    .actions((self) => {
      const env = getEnvironment(self);

      const putInStore = (elements: S[]): T[] => {
        const idsSet = new Set(elements.map((el) => '' + el[identifier]));
        elements.forEach((element) => {
          const key = '' + element[identifier];
          if (self.datas.has(key)) {
            applySnapshot(self.datas.get(key), element);
          } else {
            self.datas.put(element);
          }
        });
        return Array.from(self.datas.values()).filter((e) => idsSet.has('' + e[identifier]));
      };

      // @ts-ignore
      const loadElement = flow(function*(itemId: number) {
        let res = null;
        try {
          // @ts-ignore
          res = yield env.api.get(baseUrl + '/' + itemId, { params: loadParams });
          self.datas.put(res);
        } catch (error) {
          console.log(error);
          return null;
        }
        return res;
      });

      const loadForIds = flow(function*(itemIds: number[]) {
        self.state = STORE_STATE.DIRTY;
        itemIds = uniq(itemIds);
        const api = env.api;
        const params = { ...loadParams, [`${identifier}s`]: Array.from(new Set(itemIds)) };
        const preparedParams = api.prepareParams(params);
        let res;
        if (preparedParams.length > MAX_GET_PARAMS_LENGTH) {
          const chunksLength = itemIds.length / (Math.trunc(preparedParams.length / MAX_GET_PARAMS_LENGTH) + 1);
          const chunks = chunk(itemIds, chunksLength);
          res = flatten(
            yield Promise.all(
              chunks.map((ids) =>
                api.get(baseUrl, {
                  params: { ...loadParams, [`${identifier}s`]: Array.from(new Set(ids)) },
                }),
              ),
            ),
          );
        } else {
          res = yield api.get(baseUrl, {
            params,
          });
        }
        putInStore(res);
      });

      const setDatasFromArray = (datas: S[]) => {
        // Perform incremental updates instead of replacing the whole map snapshot.
        // This avoids creating a huge intermediate object and reduces MST reconciliation churn.
        const incomingIds = new Set<string>(datas.map((d) => '' + d[identifier]));

        // Delete entries that are not present in incoming data
        for (const existingKey of Array.from(self.datas.keys())) {
          if (!incomingIds.has(existingKey)) {
            self.datas.delete(existingKey);
          }
        }

        // Upsert incoming items
        datas.forEach((d) => {
          const key = '' + d[identifier];
          if (self.datas.has(key)) {
            applySnapshot(self.datas.get(key), d);
          } else {
            self.datas.put(d);
          }
        });
      };

      const loadList = flow(function*(options?: any) {
        var loadingStatus = new EventEmitter();
        self.miscDatas['loadingStatus'] = loadingStatus;
        loadingStatus.emit('loading');

        self.state = STORE_STATE.LOADING;

        const storeName = self['$treenode']['_subpath'];
        const res: any = yield env.api.get(baseUrl, {
          ...options,
          params: { ...(options || {}).params, ...loadParams },
        });
        let resultIds;
        if (res != null) {
          setDatasFromArray(res!);
          resultIds = res!.map((r) => r[identifier]);
        }
        const result = self.dataList.filter((d) => resultIds.indexOf(d[identifier]) >= 0);
        self.state = STORE_STATE.LOADED;

        loadingStatus.emit('loaded');
        return result;
      });

      const loadListSafely = flow(function*(waitIfAlreadyLoading: boolean, loadIfNotLoadedYet: boolean) {
        const loadingStatus = self.miscDatas['loadingStatus'];
        if (self.state === STORE_STATE.LOADED) {
          // ok nothing to do, data is ready
        } else if (self.state === STORE_STATE.LOADING) {
          if (waitIfAlreadyLoading && loadingStatus) {
            // wait if it actually loaded
            yield new Promise((resolve) => loadingStatus.once('loaded', resolve));
          } else {
            // data not ready but it is not required to wait
            return undefined;
          }
        } else {
          // start and wait the loading
          //yield loadList();
          yield checkListLoaded();
        }
        return self.dataList;
      });

      const checkListLoaded = flow(function*(options?: any) {
        if (self.state === STORE_STATE.LOADED) return true;
        if (self.state === STORE_STATE.LOADING) return false;
        return loadList();
      });

      const ensureLoaded = flow(function*(id: number) {
        const data = self.getById(id);
        if (!data) {
          yield loadElement(id);
        }
      });

      const createElement = flow(function*(data: any) {
        const result = yield env.api.post(baseUrl, data);
        if (result != null) {
          self.datas.put(result);
          NotificationToast.showSuccess('Data saved successfully');
        }
        return result;
      });

      const updateElement = flow(function*(data: S) {
        if (data == null) {
          return null;
        }
        const result = yield env.api.put(baseUrl + '/' + data[identifier], data);
        self.datas.put(result);
        NotificationToast.showSuccess('Data saved successfully');
        return result;
      });

      // @ts-ignore
      const saveElement = flow(function*(data: any) {
        let res = null;
        if (data[identifier] === undefined || data[identifier] <= 0) {
          // @ts-ignore
          res = yield createElement(data);
        } else {
          // @ts-ignore
          res = yield updateElement(data);
        }
        return res;
      });

      // @ts-ignore
      const deleteList = flow(function*(ids: number[]) {
        try {
          // @ts-ignore
          const result: string[] = yield env.api.deleteList(baseUrl, ids);
          const undeleteIds = Object.keys(result);
          ids = ids.filter((elem) => undeleteIds.indexOf(elem.toString()) === -1);
          ids.map((id) => {
            self.datas.delete(id.toString());
          });
        } catch (error) {
          console.error('An error has occurred : ', error);
          return error;
        }
      });

      const deleteElement = flow(function*(data: any): Generator<Promise<any>, void, any> {
        yield env.api.delete(baseUrl, data[identifier]);
        self.datas.delete(data[identifier]);
      });

      const clearData = () => {
        self.datas.clear();
        self.state = STORE_STATE.DIRTY;
      };

      return {
        loadElement,
        loadList,
        loadForIds,
        setDatasFromArray,
        createElement,
        updateElement,
        saveElement,
        deleteList,
        deleteElement,
        putInStore,
        clearData,
        ensureLoaded,
        checkListLoaded,
        loadListSafely,
      };
    });
}
